import { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useATMStore } from '../store/atmStore'
import { useTheme } from '../hooks/useTheme'
import type { ATMAlert, AlertPriority, ATMZone, EventSeverity } from '@atmvision/types'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

const PRIORITY_COLOR: Record<AlertPriority, string> = {
  P1: '#ef4444', P2: '#f97316', P3: '#F89421', P4: '#6b7280',
}

const SEVERITY_COLOR: Record<EventSeverity, string> = {
  CRITICAL: '#ef4444', HIGH: '#f97316', WARNING: '#F89421', INFO: '#02AFF0', OK: '#22c55e',
}

const COLLECT_API = import.meta.env.VITE_COLLECTION_API_URL || 'http://localhost:8002'

const ZONES: Array<ATMZone | 'ALL'> = ['ALL', 'Distrito Nacional', 'Norte', 'Sur', 'Este']
const PRIORITIES: Array<AlertPriority | 'ALL'> = ['ALL', 'P1', 'P2', 'P3', 'P4']

type AlertTab = 'active' | 'resolved'

function timeAgo(iso: string) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es })
  } catch {
    return iso
  }
}

export default function AlertConsole() {
  const C = useTheme()
  const { alerts, fleet, events, updateAlert } = useATMStore()
  const [tab, setTab] = useState<AlertTab>('active')
  const [zoneFilter, setZoneFilter] = useState<ATMZone | 'ALL'>('ALL')
  const [priorityFilter, setPriorityFilter] = useState<AlertPriority | 'ALL'>('ALL')
  const [selectedAlert, setSelectedAlert] = useState<ATMAlert | null>(null)

  const handleResolve = async (alertId: string) => {
    if (!alertId.startsWith('sim-')) {
      try {
        await fetch(`${COLLECT_API}/api/v1/alerts/${alertId}/resolve`, { method: 'POST' })
      } catch { /* fire-and-forget */ }
    }
    updateAlert(alertId, { status: 'RESOLVED', resolved_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    setSelectedAlert(null)
  }

  const filtered = useMemo(() => {
    return alerts.filter((a) => {
      const isActive = a.status === 'OPEN' || a.status === 'ACKNOWLEDGED' || a.status === 'SILENCED'
      if (tab === 'active' && !isActive) return false
      if (tab === 'resolved' && a.status !== 'RESOLVED') return false
      if (zoneFilter !== 'ALL' && a.zone !== zoneFilter) return false
      if (priorityFilter !== 'ALL' && a.priority !== priorityFilter) return false
      return true
    }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  }, [alerts, tab, zoneFilter, priorityFilter])

  const counts = useMemo(() => ({
    P1: alerts.filter((a) => a.priority === 'P1' && a.status !== 'RESOLVED').length,
    P2: alerts.filter((a) => a.priority === 'P2' && a.status !== 'RESOLVED').length,
    P3: alerts.filter((a) => a.priority === 'P3' && a.status !== 'RESOLVED').length,
    P4: alerts.filter((a) => a.priority === 'P4' && a.status !== 'RESOLVED').length,
  }), [alerts])

  const recentEventCount = events.filter((e) => {
    const d = new Date(e.ts)
    return Date.now() - d.getTime() < 300000
  }).length

  // Detail panel data
  const atm = selectedAlert
    ? fleet.find((d) => d.terminal_id === selectedAlert.term_id)
    : null

  const relatedEvents = selectedAlert
    ? events
        .filter((e) =>
          e.term_id === selectedAlert.term_id &&
          (e.canonical_code === selectedAlert.canonical_code ||
            selectedAlert.triggering_events.includes(e.event_id))
        )
        .slice(0, 5)
    : []

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.dark }}>
      {/* Top bar */}
      <div style={{ padding: '10px 16px', background: C.card, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['P1', 'P2', 'P3', 'P4'] as AlertPriority[]).map((p) => (
            <div key={p} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px',
              background: `${PRIORITY_COLOR[p]}18`, border: `1px solid ${PRIORITY_COLOR[p]}40`,
              borderRadius: 6,
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: PRIORITY_COLOR[p] }}>{p}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{counts[p]}</span>
            </div>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: C.muted }}>
            Eventos últimos 5 min: <strong style={{ color: recentEventCount > 0 ? C.orange : C.muted }}>{recentEventCount}</strong>
          </span>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '10px 16px', background: C.card2, borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
        <div style={{ display: 'flex', background: C.card, borderRadius: 6, overflow: 'hidden', border: `1px solid ${C.border}` }}>
          {(['active', 'resolved'] as AlertTab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                background: tab === t ? C.primary : 'transparent',
                color: tab === t ? '#fff' : C.muted, border: 'none',
                padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
              }}
            >
              {t === 'active' ? 'Activas' : 'Resueltas'}
            </button>
          ))}
        </div>

        <select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value as ATMZone | 'ALL')}
          style={{ background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 8px', fontSize: 12, fontFamily: 'inherit' }}>
          {ZONES.map((z) => <option key={z} value={z}>{z === 'ALL' ? 'Todas las Zonas' : z}</option>)}
        </select>

        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value as AlertPriority | 'ALL')}
          style={{ background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 8px', fontSize: 12, fontFamily: 'inherit' }}>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p === 'ALL' ? 'Todas las Prioridades' : p}</option>)}
        </select>

        <span style={{ fontSize: 12, color: C.muted, marginLeft: 'auto' }}>
          {filtered.length} {tab === 'active' ? 'alertas activas' : 'resueltas'}
        </span>
      </div>

      {/* Alert List */}
      <div style={{ flex: 1, overflow: 'auto', padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: C.muted, fontSize: 14 }}>
            {tab === 'active' ? 'Sin alertas activas con los filtros aplicados' : 'Sin alertas resueltas'}
          </div>
        ) : (
          filtered.map((alert) => (
            <AlertCard
              key={alert.alert_id}
              alert={alert}
              onSelect={() => setSelectedAlert(alert)}
              onAcknowledge={() => updateAlert(alert.alert_id, { status: 'ACKNOWLEDGED', updated_at: new Date().toISOString() })}
              onSilence={() => updateAlert(alert.alert_id, { status: 'SILENCED', updated_at: new Date().toISOString() })}
              onResolve={() => handleResolve(alert.alert_id)}
            />
          ))
        )}
      </div>

      {/* Single detail portal — rendered once at the AlertConsole level */}
      {selectedAlert && createPortal(
        <div
          onClick={() => setSelectedAlert(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(8,16,26,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 440,
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: '85vh',
              overflow: 'auto',
              background: C.card2,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
              padding: 16,
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>
                  Detalle de alerta ATM
                </div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 18, color: C.text, fontWeight: 800 }}>
                  {selectedAlert.term_id}
                </div>
              </div>
              <button
                onClick={() => setSelectedAlert(null)}
                style={{
                  background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`,
                  color: C.muted, borderRadius: 6, width: 28, height: 28,
                  cursor: 'pointer', fontSize: 18, lineHeight: '26px', flexShrink: 0,
                }}
              >×</button>
            </div>

            {/* Priority / code banner */}
            {(() => {
              const color = PRIORITY_COLOR[selectedAlert.priority]
              return (
                <div style={{ background: `${color}12`, border: `1px solid ${color}44`, borderRadius: 8, padding: 10, marginBottom: 14 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ background: `${color}22`, border: `1px solid ${color}`, color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 800 }}>
                      {selectedAlert.priority}
                    </span>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: SEVERITY_COLOR[selectedAlert.severity] ?? C.orange, fontWeight: 700 }}>
                      {selectedAlert.canonical_code}
                    </span>
                    <span style={{ fontSize: 11, color: C.muted }}>{selectedAlert.severity}</span>
                  </div>
                  <div style={{ fontSize: 13, color: C.text, lineHeight: 1.45 }}>{selectedAlert.description}</div>
                </div>
              )
            })()}

            {/* ATM fields grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              <DetailItem label="Modelo"       value={selectedAlert.model} />
              <DetailItem label="Zona"         value={selectedAlert.zone} />
              <DetailItem label="Estado alerta" value={selectedAlert.status} />
              <DetailItem label="Estado ATM"   value={atm?.status ?? 'No disponible'} />
              <DetailItem label="Brigada"      value={selectedAlert.assigned_brigada ?? atm?.brigada ?? 'No asignada'} />
              <DetailItem label="Imagen SO"    value={atm?.so_image ?? 'Sin imagen'} />
              <DetailItem label="Provincia"    value={atm?.province ?? 'No disponible'} />
              <DetailItem label="Municipio"    value={atm?.municipality ?? 'No disponible'} />
              <DetailItem label="Funcionalidad" value={atm?.funcionalidad ?? 'No disponible'} />
              <DetailItem label="Acceso"       value={atm?.acceso ?? 'No disponible'} />
              <DetailItem label="Serial"       value={atm?.serial_number ?? 'No disponible'} />
              <DetailItem label="Última señal" value={atm?.last_seen ? timeAgo(atm.last_seen) : 'No disponible'} />
            </div>

            {atm?.address && (
              <div style={{ marginBottom: 14 }}>
                <DetailItem label="Dirección" value={atm.address} full />
              </div>
            )}

            {/* Device chips */}
            {atm?.devices && atm.devices.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <SectionTitle label="Dispositivos ATM" />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {atm.devices.map((d) => (
                    <span key={d.component} style={{
                      fontSize: 11,
                      color: d.status === 'OK' ? C.green : d.status === 'FAULT' ? C.red : C.orange,
                      background: C.card, border: `1px solid ${C.border}`,
                      borderRadius: 999, padding: '3px 8px',
                    }}>
                      {d.component}: {d.status}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Triggering event IDs */}
            <div style={{ marginBottom: 14 }}>
              <SectionTitle label="Eventos detonantes" />
              {selectedAlert.triggering_events.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {selectedAlert.triggering_events.map((id) => (
                    <div key={id} style={{
                      fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: C.text,
                      background: C.card, border: `1px solid ${C.border}`,
                      borderRadius: 5, padding: '5px 7px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{id}</div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: C.muted }}>Sin eventos detonantes asociados</div>
              )}
            </div>

            {/* Related recent events */}
            <div style={{ marginBottom: 14 }}>
              <SectionTitle label="Eventos recientes relacionados" />
              {relatedEvents.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {relatedEvents.map((ev) => (
                    <div key={ev.event_id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: C.blue }}>{ev.raw_code}</span>
                        <span style={{ fontSize: 10, color: C.muted }}>{timeAgo(ev.ts)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: C.text, lineHeight: 1.35 }}>{ev.description}</div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{ev.event_type} · {ev.collection_strategy}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: C.muted }}>Sin eventos recientes relacionados en memoria</div>
              )}
            </div>

            {/* Action buttons */}
            {selectedAlert.status !== 'RESOLVED' && (
              <div style={{ display: 'flex', gap: 8, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                {selectedAlert.status === 'OPEN' && (
                  <ActionBtn
                    label="Reconocer"
                    onClick={() => {
                      updateAlert(selectedAlert.alert_id, { status: 'ACKNOWLEDGED', updated_at: new Date().toISOString() })
                      setSelectedAlert(null)
                    }}
                    variant="primary"
                  />
                )}
                {selectedAlert.status !== 'SILENCED' && (
                  <ActionBtn
                    label="Silenciar"
                    onClick={() => {
                      updateAlert(selectedAlert.alert_id, { status: 'SILENCED', updated_at: new Date().toISOString() })
                      setSelectedAlert(null)
                    }}
                    variant="neutral"
                  />
                )}
                <ActionBtn
                  label="Resolver"
                  onClick={() => handleResolve(selectedAlert.alert_id)}
                  variant="success"
                />
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

// ── AlertCard ─────────────────────────────────────────────────────────────────

function AlertCard({ alert, onSelect, onAcknowledge, onSilence, onResolve }: {
  alert: ATMAlert
  onSelect: () => void
  onAcknowledge: () => void
  onSilence: () => void
  onResolve: () => void
}) {
  const C = useTheme()
  const isResolved = alert.status === 'RESOLVED'
  const color = PRIORITY_COLOR[alert.priority]

  return (
    <div
      onClick={onSelect}
      style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 8, padding: '12px 14px',
        opacity: isResolved ? 0.65 : 1,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ background: `${color}22`, border: `1px solid ${color}`, color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
            {alert.priority}
          </span>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, color: C.text }}>
            {alert.term_id}
          </span>
          <span style={{ fontSize: 11, color: C.muted }}>{alert.model}</span>
          <span style={{ fontSize: 11, background: 'rgba(2,175,240,0.1)', color: C.blue, padding: '1px 7px', borderRadius: 999 }}>
            {alert.zone}
          </span>
          <span style={{
            fontSize: 10, padding: '2px 7px', borderRadius: 999, fontWeight: 600,
            background: isResolved ? 'rgba(34,197,94,0.12)' : alert.status === 'ACKNOWLEDGED' ? 'rgba(248,148,33,0.12)' : 'rgba(239,68,68,0.12)',
            color: isResolved ? C.green : alert.status === 'ACKNOWLEDGED' ? C.orange : C.red,
          }}>
            {alert.status}
          </span>
          <span style={{ fontSize: 10, color: C.muted }}>Click para detalle</span>
        </div>
        <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, marginLeft: 8 }}>
          {timeAgo(alert.created_at)}
        </span>
      </div>

      <div style={{ marginBottom: 6 }}>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: SEVERITY_COLOR[alert.severity] ?? C.orange, marginRight: 8 }}>
          {alert.canonical_code}
        </span>
        <span style={{ fontSize: 13, color: C.text }}>{alert.description}</span>
      </div>

      {alert.assigned_brigada && (
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
          Brigada: <strong style={{ color: C.text }}>{alert.assigned_brigada}</strong>
        </div>
      )}

      {alert.resolution_notes && (
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontStyle: 'italic' }}>
          {alert.resolution_notes}
        </div>
      )}

      {!isResolved && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          {alert.status === 'OPEN' && (
            <ActionBtn label="Reconocer" onClick={onAcknowledge} variant="primary" />
          )}
          {alert.status !== 'SILENCED' && (
            <ActionBtn label="Silenciar" onClick={onSilence} variant="neutral" />
          )}
          <ActionBtn label="Resolver" onClick={onResolve} variant="success" />
        </div>
      )}
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function DetailItem({ label, value, full = false }: { label: string; value: string; full?: boolean }) {
  const C = useTheme()
  return (
    <div style={{ gridColumn: full ? '1 / -1' : undefined }}>
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 12, color: C.text, lineHeight: 1.35 }}>{value}</div>
    </div>
  )
}

function SectionTitle({ label }: { label: string }) {
  const C = useTheme()
  return (
    <div style={{ fontSize: 10, color: C.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
      {label}
    </div>
  )
}

function ActionBtn({ label, onClick, variant }: { label: string; onClick: () => void; variant: 'primary' | 'neutral' | 'success' }) {
  const C = useTheme()
  // neutral uses theme tokens so the button is visible in both dark & light mode
  const colors = {
    primary: { bg: 'rgba(2,175,240,0.12)', border: 'rgba(2,175,240,0.4)', color: C.blue },
    neutral: { bg: C.tint,                 border: C.border,               color: C.muted },
    success: { bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.4)', color: C.green },
  }[variant]

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick() }}
      style={{
        background: colors.bg, border: `1px solid ${colors.border}`, color: colors.color,
        padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 11,
        fontFamily: 'inherit', fontWeight: 500,
      }}
    >
      {label}
    </button>
  )
}
